import { useCallback, useEffect, useEffectEvent, useRef, useState } from "react";
import { useLocalStorageState } from "./useLocalStorageState";
import type { ListPagination, PageResponse } from "../lib/pagination";

export interface UseAsyncPaginatedDataOptions<T = unknown> {
  /**
   * Items per page. May change at runtime (e.g. a user-facing rows-per-page control) — a
   * change clears the API page cache and resets to page 1. In local mode this needs no
   * re-fetch: the already-loaded items are simply re-sliced with the new size.
   */
  pageSize?: number;
  /**
   * When this value changes, the page cache and any prefetch result are cleared
   * and the hook restarts from page 1. Wire to a debounced search query.
   */
  key?: string;
  /** When false, no fetches are made. Transitioning to true fires the initial fetch. */
  enabled?: boolean;
  /**
   * Fetch up to this many items in a single request on mount (and on key change).
   * When the response total fits within this limit, all subsequent filtering and
   * pagination run locally with no further API calls.
   */
  prefetchLimit?: number;
  /**
   * Applied in-memory to the prefetched items when all data is loaded locally.
   * Stored in a ref — the caller does not need to memoize it.
   * Use the raw (un-debounced) query here for instant feedback while the user types.
   */
  localFilter?: (item: T) => boolean;
  /**
   * localStorage key for stale-while-revalidate on the prefetch result.
   * Pre-populates data with no loading spinner on re-visit; a background fetch always runs.
   * Scope to the user to prevent cross-user bleed: `rc_<resource>_${userId}`.
   */
  prefetchCacheKey?: string;
}

export interface UsePaginatedDataResult<T> {
  items: T[];
  total: number;
  totalPages: number;
  page: number;
  /** True only during the first fetch per key. Background polling refreshes leave this false
   *  so existing rows stay visible while new data loads silently. */
  loading: boolean;
  error: string | null;
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  /** Clears the page cache and prefetch result, resets to page 1, and re-fetches. */
  refresh: () => Promise<void>;
  /** Position and page controls for a list — pass to DataSequence's `pagination`. Add
   *  `pageSizeOptions`/`onPageSizeChange` when the page size is user-selectable. */
  pagination: ListPagination;
}

/**
 * Fetches paginated data from an async source, caching already-fetched pages so that
 * back-navigation is instant. When `prefetchLimit` is set, an initial bulk fetch loads
 * up to that many items; if all items fit, subsequent filtering (`localFilter`) and
 * pagination run entirely in-memory with no further API calls.
 *
 * `fn` is stored in a ref — callers do not need to memoize it.
 * `localFilter` is also ref-tracked — the latest version is always used inline on each render
 * so local filtering reacts immediately to query changes without triggering extra fetches.
 *
 * Side effects: DB reads via `fn`; writes to localStorage when `prefetchCacheKey` is set.
 */
export function useAsyncPaginatedData<T>(
  fn: (page: { limit: number; offset: number }) => Promise<PageResponse<T>>,
  options: UseAsyncPaginatedDataOptions<T> = {}
): UsePaginatedDataResult<T> {
  const {
    pageSize = 20,
    key,
    enabled = true,
    prefetchLimit,
    localFilter,
    prefetchCacheKey,
  } = options;

  const fnRef = useRef(fn);
  fnRef.current = fn;
  const localFilterRef = useRef(localFilter);
  localFilterRef.current = localFilter;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const pageCache = useRef(new Map<number, T[]>());

  // Prefetch result — backed by localStorage for stale-while-revalidate when prefetchCacheKey is set.
  // setPrefetchResult(null) clears both memory and the localStorage entry (e.g. on refresh/key change).
  const [prefetchResult, setPrefetchResult] = useLocalStorageState<PageResponse<T>>(
    prefetchCacheKey ?? null
  );
  const hasCachedPrefetch = useRef(prefetchResult !== null);

  const [page, setPage] = useState(1);
  const [apiItems, setApiItems] = useState<T[]>([]);
  const [apiTotal, setApiTotal] = useState(0);
  // Start in loading state only when prefetchLimit is set and no SWR cache is available.
  const [loading, setLoading] = useState(prefetchLimit !== undefined && !hasCachedPrefetch.current);
  // Tracks whether the first successful fetch has completed for the current key. Used to suppress
  // the loading spinner and item-clearing on background polling refreshes — not state because it
  // does not need to trigger a render.
  const hasLoadedOnceRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  // Have all items been loaded in one go?
  const allLoaded = prefetchResult !== null && prefetchResult.items.length >= prefetchResult.total;

  // ── Per-page fetch (API path) ─────────────────────────────────────────────────
  const fetchPage = useCallback(
    async (targetPage: number) => {
      if (pageCache.current.has(targetPage)) {
        setApiItems(pageCache.current.get(targetPage)!);
        setPage(targetPage);
        return;
      }
      if (!hasLoadedOnceRef.current) setLoading(true);
      setError(null);
      try {
        const result = await fnRef.current({
          limit: pageSize,
          offset: (targetPage - 1) * pageSize,
        });
        pageCache.current.set(targetPage, result.items);
        setApiTotal(result.total);
        setApiItems(result.items);
        setPage(targetPage);
        hasLoadedOnceRef.current = true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load");
      } finally {
        setLoading(false);
      }
    },
    [pageSize]
  );

  // ── Bulk prefetch (local mode entry point) ───────────────────────────────────
  const doPrefetch = useCallback(async () => {
    if (prefetchLimit === undefined) return;
    if (!hasLoadedOnceRef.current && !hasCachedPrefetch.current) setLoading(true);
    setError(null);
    let result: PageResponse<T>;
    try {
      result = await fnRef.current({ limit: prefetchLimit, offset: 0 });
      hasCachedPrefetch.current = true;
      setPrefetchResult(result); // also writes through to localStorage when prefetchCacheKey is set
      hasLoadedOnceRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
      setLoading(false);
      return;
    }
    if (result.items.length >= result.total) {
      // All items loaded — local mode; no further API call needed.
      setLoading(false);
    } else {
      // More items exist — hand off to the per-page API path.
      // fetchPage manages setLoading(false) in its own finally block.
      void fetchPageRef.current(1);
    }
  }, [prefetchLimit, setPrefetchResult]);

  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;
  const doPrefetchRef = useRef(doPrefetch);
  doPrefetchRef.current = doPrefetch;

  // startFetchRef tracks the latest start-fetch logic so effects can call it
  // without stale closures — same pattern as fetchPageRef / doPrefetchRef.
  const startFetchRef = useRef<() => void>(() => {});
  startFetchRef.current = () => {
    if (prefetchLimit !== undefined) void doPrefetchRef.current();
    else void fetchPageRef.current(1);
  };

  // ── On mount ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabledRef.current) return;
    startFetchRef.current();
  }, []);

  // ── enabled transition: false → true ─────────────────────────────────────────
  const prevEnabled = useRef(enabled);
  useEffect(() => {
    if (enabledRef.current && !prevEnabled.current) startFetchRef.current();
    prevEnabled.current = enabledRef.current;
  }, [enabled]);

  // ── Key change: clear cache + prefetch and restart ────────────────────────────
  const isFirstKeyRender = useRef(true);
  useEffect(() => {
    if (isFirstKeyRender.current) {
      isFirstKeyRender.current = false;
      return;
    }
    pageCache.current.clear();
    setPrefetchResult(null);
    hasCachedPrefetch.current = false;
    hasLoadedOnceRef.current = false;
    setPage(1);
    setApiItems([]);
    if (enabledRef.current) startFetchRef.current();
  }, [key, setPrefetchResult]);

  // ── pageSize change: clear the API page cache and restart from page 1 ───────
  // Local mode needs no re-fetch — displayItems is re-sliced with the new pageSize below.
  // Triggered by pageSize changing, but needs the *latest* allLoaded without re-running whenever
  // allLoaded itself changes (that would wrongly reset pagination mid-prefetch) — an Effect Event.
  const onPageSizeChange = useEffectEvent(() => {
    pageCache.current.clear();
    setPage(1);
    if (!allLoaded && enabledRef.current) {
      setApiItems([]);
      void fetchPageRef.current(1);
    }
  });
  const isFirstPageSizeRender = useRef(true);
  useEffect(() => {
    if (isFirstPageSizeRender.current) {
      isFirstPageSizeRender.current = false;
      return;
    }
    onPageSizeChange();
  }, [pageSize]);

  // ── Refresh ───────────────────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    pageCache.current.clear();
    setPrefetchResult(null);
    hasCachedPrefetch.current = false;
    setPage(1);
    // Keep existing items visible during a background polling refresh so the table doesn't
    // flash empty; only blank the list on the first load (hasLoadedOnceRef not yet true).
    if (!hasLoadedOnceRef.current) setApiItems([]);
    if (prefetchLimit !== undefined) await doPrefetchRef.current();
    else await fetchPageRef.current(1);
  }, [prefetchLimit, setPrefetchResult]);

  // ── Derive displayed items ────────────────────────────────────────────────────
  // In local mode, compute inline on each render so localFilter changes (e.g. the user
  // typing before debounce fires) are reflected immediately without extra state.
  const filter = localFilterRef.current;
  const localFiltered = allLoaded
    ? filter
      ? prefetchResult!.items.filter(filter)
      : prefetchResult!.items
    : null;

  const displayItems =
    localFiltered !== null ? localFiltered.slice((page - 1) * pageSize, page * pageSize) : apiItems;
  const displayTotal = localFiltered !== null ? localFiltered.length : apiTotal;
  const displayTotalPages = Math.max(1, Math.ceil(displayTotal / pageSize));

  // ── Navigation ────────────────────────────────────────────────────────────────
  function goToPage(targetPage: number) {
    if (allLoaded) setPage(targetPage);
    else void fetchPageRef.current(targetPage);
  }

  return {
    items: displayItems,
    total: displayTotal,
    totalPages: displayTotalPages,
    page,
    loading,
    error,
    goToPage,
    nextPage: () => {
      if (page < displayTotalPages) goToPage(page + 1);
    },
    prevPage: () => {
      if (page > 1) goToPage(page - 1);
    },
    hasNextPage: page < displayTotalPages,
    hasPrevPage: page > 1,
    refresh,
    pagination: { total: displayTotal, page, pageSize, onPageChange: goToPage },
  };
}
